import { createRoot, type Root } from "react-dom/client"
import {
  applyThemeVars,
  createShadowHost,
  type ShadowHost,
} from "../mount/createShadowHost"
import { waitForSelector } from "../mount/waitForSelector"
import type { BppSearchConfig, InstanceConfig, TriggerConfig } from "../types"
import { normalizeConfig, resolvePlaceholder, type NormalizedBppSearchConfig } from "../config"
import { ModalController } from "./ModalController"
import { AgentAutocomplete } from "./AgentAutocomplete"
import { PropertyAutocomplete } from "./PropertyAutocomplete"
import { IconSearch } from "./icons"

type CleanupFn = () => void

export class EmbedController {
  private readonly config: NormalizedBppSearchConfig
  private readonly modal: ModalController
  private readonly cleanups: CleanupFn[] = []
  private readonly embeddedRoots: Root[] = []
  private readonly embeddedHosts: ShadowHost[] = []

  constructor(config: BppSearchConfig) {
    this.config = normalizeConfig(config)
    this.modal = new ModalController(this.config)
  }

  async mount(): Promise<void> {
    if (this.config.ctaPresentation === "external") {
      await Promise.all(
        this.config.triggers.map((trigger) => this.bindExternalTrigger(trigger))
      )
      return
    }
    await Promise.all(
      this.config.instances.map((instance) => this.mountEmbeddedInstance(instance))
    )
  }

  destroy(): void {
    this.modal.close()
    this.cleanups.forEach((cleanup) => cleanup())
    this.cleanups.length = 0
    this.embeddedRoots.forEach((root) => root.unmount())
    this.embeddedRoots.length = 0
    this.embeddedHosts.forEach(({ host }) => host.remove())
    this.embeddedHosts.length = 0
  }

  private async bindExternalTrigger(trigger: TriggerConfig): Promise<void> {
    const element = await waitForSelector(
      trigger.selector,
      this.config.triggerRetryMs,
      this.config.triggerRetryTimeoutMs
    )
    if (!(element instanceof HTMLElement)) {
      return
    }

    const handler = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()
      this.modal.open(
        {
          mode: trigger.mode,
          defaultTab: trigger.defaultTab,
          tabs: trigger.tabs,
          ui: trigger.ui,
          placeholder: trigger.placeholder,
          zIndex: trigger.zIndex ?? this.config.zIndex,
        },
        element
      )
    }

    element.addEventListener("click", handler)
    this.cleanups.push(() => element.removeEventListener("click", handler))
  }

  private async mountEmbeddedInstance(instance: InstanceConfig): Promise<void> {
    const container = await waitForSelector(
      instance.mount,
      this.config.triggerRetryMs,
      this.config.triggerRetryTimeoutMs
    )
    if (!(container instanceof HTMLElement)) {
      console.warn(`[BppSearch] Mount not found: ${instance.mount}`)
      return
    }

    const shadowHost = createShadowHost(container)
    applyThemeVars(shadowHost.mount, this.config.theme)
    this.embeddedHosts.push(shadowHost)

    const root = createRoot(shadowHost.mount)
    this.embeddedRoots.push(root)

    const label = resolvePlaceholder(
      instance.mode,
      instance.defaultTab,
      instance.placeholder,
      this.config.placeholders
    )

    if (instance.ui === "dropdown") {
      root.render(
        instance.mode === "property" ? (
          <PropertyAutocomplete
            searchBaseUrl={this.config.searchBaseUrl}
            variant="dropdown"
            defaultTab={instance.defaultTab ?? "buy"}
            tabs={instance.tabs}
            placeholders={this.config.placeholders}
            placeholder={label}
            onClose={() => undefined}
            onRedirect={this.modal.redirect}
            prepareNewTab={this.modal.prepareNewTab}
          />
        ) : (
          <AgentAutocomplete
            searchBaseUrl={this.config.searchBaseUrl}
            variant="dropdown"
            entityType={instance.mode === "office" ? "office" : "agent"}
            placeholder={label}
            onClose={() => undefined}
            onRedirect={this.modal.redirect}
          />
        )
      )
      return
    }

    root.render(
      <button
        type="button"
        className="bpp-trigger"
        onClick={() =>
          this.modal.open(
            {
              mode: instance.mode,
              defaultTab: instance.defaultTab,
              tabs: instance.tabs,
              ui: instance.ui,
              placeholder: instance.placeholder,
              zIndex: instance.zIndex ?? this.config.zIndex,
            },
            null
          )
        }
      >
        <IconSearch />
        <span>{label}</span>
      </button>
    )
  }
}
